import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  Checkbox,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Check,
  Close,
  HourglassEmpty,
  SelectAll,
  Deselect
} from '@mui/icons-material';
import { bookkeepersAPI } from '../services/api';

const PendingApprovals = () => {
  const [pendingBookkeepers, setPendingBookkeepers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedBookkeepers, setSelectedBookkeepers] = useState([]);
  
  const [approvalDialog, setApprovalDialog] = useState({
    open: false,
    bookkeeperId: null,
    bookkeeperName: '',
    expirationMonths: 6
  });
  
  const [rejectionDialog, setRejectionDialog] = useState({
    open: false,
    bookkeeperId: null,
    bookkeeperName: '',
    rejectionReason: ''
  });

  const [bulkApprovalDialog, setBulkApprovalDialog] = useState({
    open: false,
    expirationMonths: 6
  });

  const fetchPendingBookkeepers = async () => {
    try {
      setLoading(true);
      const response = await bookkeepersAPI.getPending();
      // console.log('Full API response:', response.data);
      
      // Handle the specific API response structure from your backend
      let bookkeepersData = [];
      
      if (response.data.data && Array.isArray(response.data.data.bookkeepers)) {
        // Your API returns data in response.data.data.bookkeepers
        bookkeepersData = response.data.data.bookkeepers;
        // console.log('Found bookkeepers:', bookkeepersData.length);
      } else if (Array.isArray(response.data)) {
        // Fallback: if response.data is directly an array
        bookkeepersData = response.data;
      } else if (response.data.bookkeepers) {
        // Fallback: if bookkeepers is directly on response.data
        bookkeepersData = response.data.bookkeepers;
      }
      
      // console.log('Processed bookkeepers data:', bookkeepersData);
      setPendingBookkeepers(bookkeepersData);
      setSelectedBookkeepers([]); // Clear selection on refresh
      setError('');
      
    } catch (err) {
      console.error('Error fetching pending bookkeepers:', err);
      setError('Failed to fetch pending bookkeepers. Please try again.');
      setPendingBookkeepers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingBookkeepers();
  }, []);

  // ✅ BETTER: Add proper error handling and limits
useEffect(() => {
  const fetchData = async () => {
    try {
      await fetchPendingBookkeepers();
    } catch (error) {
      if (error.response?.status === 429) {
        // Wait before retrying
        setTimeout(() => fetchData(), 5000);
      }
    }
  };
  fetchData();
}, []);

  const handleApprove = async () => {
    try {
      const response = await bookkeepersAPI.approve(approvalDialog.bookkeeperId, {
        expirationMonths: approvalDialog.expirationMonths
      });
      
      // Handle both success: true and success: false responses
      if (response.data.success === true || response.data.message) {
        setSuccess(`Bookkeeper ${approvalDialog.bookkeeperName} approved successfully!`);
      } else {
        setError(response.data.message || 'Failed to approve bookkeeper');
      }
      
      setApprovalDialog({ open: false, bookkeeperId: null, bookkeeperName: '', expirationMonths: 6 });
      fetchPendingBookkeepers(); // Refresh the list
    } catch (err) {
      setError('Failed to approve bookkeeper');
      console.error('Error approving bookkeeper:', err);
    }
  };

  const handleReject = async () => {
    try {
      const response = await bookkeepersAPI.reject(rejectionDialog.bookkeeperId, {
        rejectionReason: rejectionDialog.rejectionReason
      });
      
      // Handle both success: true and success: false responses
      if (response.data.success === true || response.data.message) {
        setSuccess(`Bookkeeper ${rejectionDialog.bookkeeperName} rejected successfully!`);
      } else {
        setError(response.data.message || 'Failed to reject bookkeeper');
      }
      
      setRejectionDialog({ open: false, bookkeeperId: null, bookkeeperName: '', rejectionReason: '' });
      fetchPendingBookkeepers(); // Refresh the list
    } catch (err) {
      setError('Failed to reject bookkeeper');
      console.error('Error rejecting bookkeeper:', err);
    }
  };

  // Bulk approval functionality
  const handleBulkApprove = async () => {
    try {
      let approvedCount = 0;
      let errorCount = 0;

      for (const id of selectedBookkeepers) {
        try {
          await bookkeepersAPI.approve(id, {
            expirationMonths: bulkApprovalDialog.expirationMonths
          });
          approvedCount++;
        } catch (err) {
          console.error(`Failed to approve bookkeeper ${id}:`, err);
          errorCount++;
        }
      }

      if (errorCount === 0) {
        setSuccess(`Successfully approved ${approvedCount} bookkeeper(s)!`);
      } else if (approvedCount > 0) {
        setSuccess(`Approved ${approvedCount} bookkeeper(s), ${errorCount} failed`);
      } else {
        setError('Failed to approve any bookkeepers');
      }

      setBulkApprovalDialog({ open: false, expirationMonths: 6 });
      setSelectedBookkeepers([]);
      fetchPendingBookkeepers();
    } catch (err) {
      setError('Failed to process bulk approval');
      console.error('Error in bulk approval:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Split fullName into firstName and lastName
  const getNameParts = (bookkeeper) => {
    if (bookkeeper.firstName && bookkeeper.lastName) {
      return { firstName: bookkeeper.firstName, lastName: bookkeeper.lastName };
    }
    if (bookkeeper.fullName) {
      const names = bookkeeper.fullName.split(' ');
      return {
        firstName: names[0] || '',
        lastName: names.slice(1).join(' ') || ''
      };
    }
    return { firstName: 'Unknown', lastName: '' };
  };

  // Selection handlers
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedBookkeepers(safeBookkeepers.map(bk => bk._id));
    } else {
      setSelectedBookkeepers([]);
    }
  };

  const handleSelectBookkeeper = (bookkeeperId) => {
    setSelectedBookkeepers(prev => 
      prev.includes(bookkeeperId)
        ? prev.filter(id => id !== bookkeeperId)
        : [...prev, bookkeeperId]
    );
  };

  // Ensure pendingBookkeepers is always an array for safe mapping
  const safeBookkeepers = Array.isArray(pendingBookkeepers) ? pendingBookkeepers : [];

  const isAllSelected = safeBookkeepers.length > 0 && selectedBookkeepers.length === safeBookkeepers.length;
  const isSomeSelected = selectedBookkeepers.length > 0 && selectedBookkeepers.length < safeBookkeepers.length;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading pending bookkeepers...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Pending Approvals
        </Typography>
        <Box display="flex" alignItems="center">
          <HourglassEmpty sx={{ mr: 1, color: 'warning.main' }} />
          <Typography variant="h6" color="warning.main">
            {safeBookkeepers.length} Pending
          </Typography>
        </Box>
      </Box>

      {/* Bulk Actions Bar */}
      {selectedBookkeepers.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.light', color: 'white' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography>
              {selectedBookkeepers.length} bookkeeper(s) selected
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                color="success"
                size="small"
                onClick={() => setBulkApprovalDialog({ open: true, expirationMonths: 6 })}
              >
                Approve Selected
              </Button>
              <Button
                variant="outlined"
                size="small"
                sx={{ color: 'white', borderColor: 'white' }}
                onClick={() => setSelectedBookkeepers([])}
              >
                Clear Selection
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={isSomeSelected}
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Phone</strong></TableCell>
              <TableCell><strong>Location</strong></TableCell>
              <TableCell><strong>Company</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Submitted Date</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {safeBookkeepers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Box py={3}>
                    <Typography variant="body1" color="textSecondary">
                      No pending bookkeepers found
                    </Typography>
                    <Button 
                      variant="outlined" 
                      onClick={fetchPendingBookkeepers}
                      sx={{ mt: 2 }}
                    >
                      Refresh
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              safeBookkeepers.map((bookkeeper, index) => {
                const { firstName, lastName } = getNameParts(bookkeeper);
                const displayName = `${firstName} ${lastName}`.trim();
                
                return (
                  <TableRow key={bookkeeper._id || `bookkeeper-${index}`} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedBookkeepers.includes(bookkeeper._id)}
                        onChange={() => handleSelectBookkeeper(bookkeeper._id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {displayName}
                      </Typography>
                    </TableCell>
                    <TableCell>{bookkeeper.email || 'No email'}</TableCell>
                    <TableCell>{bookkeeper.phone || 'N/A'}</TableCell>
                    <TableCell>
                      {bookkeeper.city && `${bookkeeper.city}, ${bookkeeper.state}`}
                      {bookkeeper.zipCode && ` ${bookkeeper.zipCode}`}
                      {!bookkeeper.city && !bookkeeper.state && 'N/A'}
                    </TableCell>
                    <TableCell>
                      {bookkeeper.companyName || 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={bookkeeper.status || 'pending'} 
                        size="small" 
                        color="warning"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{formatDate(bookkeeper.createdAt)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          startIcon={<Check />}
                          onClick={() => setApprovalDialog({
                            open: true,
                            bookkeeperId: bookkeeper._id,
                            bookkeeperName: displayName,
                            expirationMonths: 6
                          })}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<Close />}
                          onClick={() => setRejectionDialog({
                            open: true,
                            bookkeeperId: bookkeeper._id,
                            bookkeeperName: displayName,
                            rejectionReason: ''
                          })}
                        >
                          Reject
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Debug info - remove in production */}
      {process.env.NODE_ENV === 'development' && safeBookkeepers.length > 0 && (
        <Box mt={2} p={2} bgcolor="grey.100" borderRadius={1}>
          <Typography variant="caption" component="div">
            <strong>Debug Info:</strong> Loaded {safeBookkeepers.length} bookkeeper(s)
          </Typography>
        </Box>
      )}

      {/* Individual Approval Dialog */}
      <Dialog 
        open={approvalDialog.open} 
        onClose={() => setApprovalDialog({ ...approvalDialog, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Approve Bookkeeper</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Approve <strong>{approvalDialog.bookkeeperName}</strong> and set expiration period:
          </Typography>
          <FormControl fullWidth variant="outlined" sx={{ mt: 2 }}>
            <InputLabel>Expiration Period</InputLabel>
            <Select
              value={approvalDialog.expirationMonths}
              onChange={(e) => setApprovalDialog({
                ...approvalDialog,
                expirationMonths: e.target.value
              })}
              label="Expiration Period"
            >
              <MenuItem value={1}>1 Month</MenuItem>
              <MenuItem value={3}>3 Months</MenuItem>
              <MenuItem value={6}>6 Months</MenuItem>
              <MenuItem value={12}>12 Months</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialog({ ...approvalDialog, open: false })}>
            Cancel
          </Button>
          <Button 
            onClick={handleApprove} 
            variant="contained" 
            color="success"
            startIcon={<Check />}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Approval Dialog */}
      <Dialog 
        open={bulkApprovalDialog.open} 
        onClose={() => setBulkApprovalDialog({ ...bulkApprovalDialog, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bulk Approve Bookkeepers</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Approve <strong>{selectedBookkeepers.length}</strong> selected bookkeeper(s) and set expiration period:
          </Typography>
          <FormControl fullWidth variant="outlined" sx={{ mt: 2 }}>
            <InputLabel>Expiration Period</InputLabel>
            <Select
              value={bulkApprovalDialog.expirationMonths}
              onChange={(e) => setBulkApprovalDialog({
                ...bulkApprovalDialog,
                expirationMonths: e.target.value
              })}
              label="Expiration Period"
            >
              <MenuItem value={1}>1 Month</MenuItem>
              <MenuItem value={3}>3 Months</MenuItem>
              <MenuItem value={6}>6 Months</MenuItem>
              <MenuItem value={12}>12 Months</MenuItem>
            </Select>
          </FormControl>
          <Alert severity="info" sx={{ mt: 2 }}>
            This will approve all selected bookkeepers with the chosen expiration period.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkApprovalDialog({ ...bulkApprovalDialog, open: false })}>
            Cancel
          </Button>
          <Button 
            onClick={handleBulkApprove} 
            variant="contained" 
            color="success"
            startIcon={<Check />}
          >
            Approve {selectedBookkeepers.length} Bookkeepers
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog 
        open={rejectionDialog.open} 
        onClose={() => setRejectionDialog({ ...rejectionDialog, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Bookkeeper</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Reject <strong>{rejectionDialog.bookkeeperName}</strong> and provide a reason:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            label="Rejection Reason"
            value={rejectionDialog.rejectionReason}
            onChange={(e) => setRejectionDialog({
              ...rejectionDialog,
              rejectionReason: e.target.value
            })}
            placeholder="Please provide a reason for rejection..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectionDialog({ ...rejectionDialog, open: false })}>
            Cancel
          </Button>
          <Button 
            onClick={handleReject} 
            variant="contained" 
            color="error"
            disabled={!rejectionDialog.rejectionReason.trim()}
            startIcon={<Close />}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PendingApprovals;